import React, { useEffect, useState } from "react";
import { Navigate, useLocation, Outlet } from "react-router-dom";
import authService from "../../Services/authService";
import Loader from "./Loader";

/**
 * ProtectedRoute Guard supporting session verification and RBAC capability checks.
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.children] - Child components to render
 * @param {string} [props.permission] - Single required permission
 * @param {string[]} [props.anyPermissions] - User must have at least one of these permissions
 * @param {string[]} [props.allPermissions] - User must have all of these permissions
 * @param {number|number[]} [props.role] - Single required role ID or array of allowed role IDs
 * @param {React.ReactNode} [props.fallback] - Custom component to render if access denied (defaults to redirect to /unauthorized)
 */
const ProtectedRoute = ({
  children,
  permission,
  anyPermissions,
  allPermissions,
  role,
  fallback,
}) => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(() => authService.getUser());
  const [isAuthenticated, setIsAuthenticated] = useState(() => authService.isAuthenticated());
  const [isChecking, setIsChecking] = useState(() => !authService.isSessionChecked());

  useEffect(() => {
    let isMounted = true;

    // If session was already checked and user is in memory, skip network call!
    if (!authService.isSessionChecked()) {
      authService.checkSession().then((result) => {
        if (isMounted) {
          setCurrentUser(result?.user || null);
          setIsAuthenticated(!!result?.authenticated);
          setIsChecking(false);
        }
      });
    } else {
      setIsChecking(false);
    }

    // Listen for session logout/revocation/hydration events
    const handleUserChanged = (e) => {
      if (isMounted) {
        setCurrentUser(e.detail?.user || null);
        setIsAuthenticated(!!e.detail?.user);
      }
    };

    window.addEventListener("auth:user_changed", handleUserChanged);

    // Prevent back button navigation when user is not authenticated
    const handlePopState = () => {
      if (!authService.isAuthenticated() && location.pathname !== "/login") {
        window.history.pushState(null, null, "/login");
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      isMounted = false;
      window.removeEventListener("auth:user_changed", handleUserChanged);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [location.pathname]);

  if (isChecking) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Capability checks
  let hasAccess = true;

  if (permission && !authService.can(permission)) {
    hasAccess = false;
  } else if (anyPermissions && anyPermissions.length > 0 && !authService.canAny(...anyPermissions)) {
    hasAccess = false;
  } else if (allPermissions && allPermissions.length > 0 && !authService.canAll(...allPermissions)) {
    hasAccess = false;
  }

  if (hasAccess && role !== undefined && !authService.hasRole(role)) {
    hasAccess = false;
  }

  if (!hasAccess) {
    return fallback || <Navigate to="/unauthorized" replace state={{ from: location }} />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;